import { useId, useState } from 'react';
import type { ReactNode } from 'react';
import { ChevronDown } from 'lucide-react';
import { cn } from '@/lib/cn';

export function AnalyticsDisclosure({
  title,
  description,
  badge,
  children,
}: {
  title: string;
  description: string;
  badge?: string;
  children: ReactNode;
}) {
  const [open, setOpen] = useState(false);
  const id = useId();
  return (
    <div
      className={cn(
        'min-w-0 rounded-2xl border bg-bg-1 transition-colors',
        open ? 'border-violet/25 shadow-card' : 'border-line-1',
      )}
    >
      <h3>
        <button
          id={`${id}-trigger`}
          type="button"
          aria-expanded={open}
          aria-controls={id}
          onClick={() => setOpen(!open)}
          className="flex min-h-16 w-full items-center gap-3 rounded-2xl px-4 py-4 text-left hover:bg-bg-0/80 sm:px-5"
        >
          <span className="min-w-0 flex-1">
            <span className="flex flex-wrap items-center gap-2">
              <span className="text-[13px] font-semibold text-text-1">{title}</span>
              {badge && (
                <span className="rounded-md bg-bg-2 px-2 py-0.5 text-[11px] font-medium text-text-3">
                  {badge}
                </span>
              )}
            </span>
            <span className="mt-1.5 block text-[11px] font-normal leading-relaxed text-text-3">
              {description}
            </span>
          </span>
          <span
            className={cn(
              'flex size-7 shrink-0 items-center justify-center rounded-lg border transition-colors',
              open
                ? 'border-violet/15 bg-violet-soft text-violet-deep'
                : 'border-line-1 bg-bg-0 text-text-3',
            )}
          >
            <ChevronDown
              className={cn('size-3.5 transition-transform', open && 'rotate-180')}
              aria-hidden
            />
          </span>
        </button>
      </h3>
      <div id={id} role="region" aria-label={title} hidden={!open}>
        {open && <div className="space-y-4 border-t border-line-1 p-3 sm:p-5">{children}</div>}
      </div>
    </div>
  );
}
